'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'

const SCORE_DESCRIPTION =
  'Our expert score factors in lab testing plus a rigorous hands-on evaluation. We wear each hearing aid for at least a week, rating build quality, battery life, Bluetooth stability, comfort, value, app features, and professional support. SoundScore is the most heavily weighted component.'

// Lucide's `info` glyph. `strokeLinecap="round"` is load-bearing, not cosmetic:
// the tittle of the "i" is the zero-length segment `M12 8h.01`, which paints
// nothing under the default butt cap — leaving a bare stem in a circle.
const InfoIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
)

interface ScoreBadgePopoverProps {
  score: number
  variant: 'solid' | 'default'
  /**
   * Which side of the badge the panel opens on. The old app used Radix, whose
   * collision detection flipped `side="top"` automatically; we have no such
   * measurement, so the caller states it. Badges pinned to the top of a card
   * must pass `bottom` — there is no room above them.
   */
  placement?: 'top' | 'bottom'
}

export const ScoreBadgePopover = ({ score, variant, placement = 'top' }: ScoreBadgePopoverProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const scoreText = score.toFixed(1)

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const isSolid = variant === 'solid'

  return (
    <div className="relative">
      <div
        className={
          isSolid
            ? 'bg-navy flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 shadow-md'
            : 'flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm'
        }
      >
        <span className={isSolid ? 'text-sm font-bold text-white' : 'text-lapis text-sm font-bold'}>{scoreText}</span>
        <span className={isSolid ? 'text-[0.8em] text-white/70' : 'text-[0.8em] text-slate-400'}>/5</span>
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggle}
          data-tracking="ignore"
          className={`ml-0.5 cursor-pointer rounded-full p-0.5 transition-colors duration-200 ${isSolid ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <InfoIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && (
        <>
          {/* Panel is end-aligned (`right-0`), not centred on the badge. The badge
              sits at the right edge of a right-hand sidebar column, so a centred
              280px panel hangs ~100px past the viewport and adds a horizontal
              scrollbar at 1024/1280. Radix used to shift it away from the edge;
              anchoring the panel's right edge to the badge's is the static
              equivalent. The arrow stays on the badge, so it lands near the end. */}
          <div
            ref={popoverRef}
            className={
              placement === 'bottom'
                ? 'bg-navy absolute top-full right-0 z-50 mt-3 w-[280px] rounded-lg p-5 text-white shadow-xl ring-1 ring-white/10'
                : 'bg-navy absolute right-0 bottom-full z-50 mb-3 w-[280px] rounded-lg p-5 text-white shadow-xl ring-1 ring-white/10'
            }
          >
            <div className="mb-2 text-base font-bold tracking-wide">
              {t('product.expertScoreLabel', 'Expert Score:')} <span className="text-[#4a7fe5]">{scoreText}/5</span>
            </div>
            <p className="text-sm leading-relaxed text-slate-200">{SCORE_DESCRIPTION}</p>
          </div>
          {/* Arrow: a CSS triangle drawn from borders on a 0x0 box. It must stay
              background-less — a background paints across the whole border box
              (background-clip is border-box), showing through the transparent
              side borders and squaring the triangle off into a rectangle. It is a
              sibling of the panel, anchored to the badge, so it keeps pointing at
              the badge no matter how the panel is aligned. */}
          <div
            aria-hidden
            className={
              placement === 'bottom'
                ? 'absolute top-full left-1/2 z-50 mt-1 h-0 w-0 -translate-x-1/2 border-x-8 border-b-8 border-x-transparent border-b-[#142546]'
                : 'absolute bottom-full left-1/2 z-50 mb-1 h-0 w-0 -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-[#142546]'
            }
          />
        </>
      )}
    </div>
  )
}
